import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { RealtimeAction } from '@war-game/engine';
import { useAuth } from '../store/auth';
import { api } from './client';

export const keys = {
  me: ['me'] as const,
  matches: ['matches'] as const,
  match: (id: string) => ['match', id] as const,
  view: (id: string) => ['view', id] as const,
  news: (id: string, filter: object) => ['news', id, filter] as const,
  messages: (id: string, channel?: string) => ['messages', id, channel ?? 'all'] as const,
  score: (id: string) => ['score', id] as const,
  combats: (id: string, turn?: number) => ['combats', id, turn ?? 'last'] as const,
  reports: (id: string) => ['reports', id] as const,
  research: (id: string) => ['research', id] as const,
  admin: ['admin'] as const,
};

export function useMatches() {
  return useQuery({
    queryKey: keys.matches,
    queryFn: () => api.call('listMatches'),
    refetchInterval: 15_000,
  });
}

export function useMatch(id: string) {
  return useQuery({
    queryKey: keys.match(id),
    queryFn: () => api.call('getMatch', { params: { id } }),
    refetchInterval: 30_000,
  });
}

export function useView(id: string, enabled = true) {
  return useQuery({
    queryKey: keys.view(id),
    queryFn: () => api.call('view', { params: { id } }),
    enabled,
    staleTime: 5_000,
    // Produção contínua: estoques e Tesouro crescem durante o turno. Relê a cada 60 s
    // (15 s em turnos de até 30 min, como o de teste de 5 min).
    refetchInterval: (query) => ((query.state.data?.config.turnMinutes ?? 1440) <= 30 ? 15_000 : 60_000),
  });
}

export function useNews(
  id: string,
  filter: { turn?: number; level?: 'headline' | 'news' | 'note'; region?: string; player?: string },
) {
  return useQuery({
    queryKey: keys.news(id, filter),
    queryFn: () => api.call('news', { params: { id }, query: filter }),
  });
}

export function useMessages(id: string, channel?: string, enabled = true) {
  return useQuery({
    queryKey: keys.messages(id, channel),
    enabled,
    queryFn: () => api.call('messages', { params: { id }, ...(channel ? { query: { channel } } : {}) }),
  });
}

export function useScore(id: string) {
  return useQuery({ queryKey: keys.score(id), queryFn: () => api.call('scoreboard', { params: { id } }) });
}

/** Histórico de combates do jogador (feitos e recebidos), do mais recente ao mais antigo. */
export function useCombatHistory(id: string) {
  return useQuery({
    queryKey: [...keys.combats(id), 'history'] as const,
    queryFn: () => api.call('combats', { params: { id }, query: { all: 1, limit: 200 } }),
  });
}

export function useCombats(id: string, turn?: number) {
  return useQuery({
    queryKey: keys.combats(id, turn),
    queryFn: () => api.call('combats', { params: { id }, ...(turn ? { query: { turn } } : {}) }),
  });
}

export function useReports(id: string) {
  return useQuery({
    queryKey: keys.reports(id),
    queryFn: () => api.call('intelReports', { params: { id } }),
  });
}

export function useResearch(id: string) {
  return useQuery({ queryKey: keys.research(id), queryFn: () => api.call('research', { params: { id } }) });
}

/** Ação em tempo real genérica (POST /actions); relê a visão ao terminar. */
export function useAct(matchId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (action: RealtimeAction) => api.call('action', { params: { id: matchId }, body: action }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: keys.view(matchId) });
      void qc.invalidateQueries({ queryKey: keys.research(matchId) });
    },
  });
}

export function useIsAdmin(): boolean {
  return useAuth((s) => s.user?.role === 'admin');
}
